import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Tab, Row, Col, Alert } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { v4 as uuidv4 } from 'uuid';
import Conversations from '../../../Models/Conversations';
import { Messages } from '../../../Models/Message';
import { 
  GetMessages, 
  postMessageInConversation,
  CloseConversation,
  DeleteConversation,
  CreateConversation
} from '../../../Services/PtaasApiService';
import { ConversationList } from './components/ConversationList';
import { MessageList } from './components/MessageList';
import { ChatHeader } from './components/ChatHeader';
import { MessageInput } from './components/MessageInput';
import { NewChatModal } from './components/NewChatModal';
import { FadeAlert } from '../Transitions';
import { useNavigate } from 'react-router-dom';

interface MessagingProps {
  conversations: Conversations[];
  onChange: () => void;
  onUnreadCountUpdate?: (convId: string) => void;
}

// Time ago utility with error handling
const timeAgo = (isoDate: string | null | undefined): string => {
  if (!isoDate) return 'unknown';
  
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return 'unknown';
  
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 0) return 'just now';
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `${mins} minute${mins > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  const days = Math.floor(diffInSeconds / 86400);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

const Messaging: React.FC<MessagingProps> = ({ conversations, onChange, onUnreadCountUpdate }) => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  
  // State
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<Messages[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [newChat, setNewChat] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Refs to prevent duplicate operations
  const urlParamsProcessed = useRef(false);
  const creatingConversation = useRef(false);
  
  const messagesPerPage = 4;
  const totalPages = Math.ceil(conversations.length / messagesPerPage);

  // User info
  const me = useMemo(() => ({
    accountId: accounts[0]?.localAccountId,
    homeId: accounts[0]?.homeAccountId,
    username: accounts[0]?.username?.toLowerCase(),
  }), [accounts]);

  // Helper: Find page number for a conversation
  const findPageForConversation = useCallback((convId: string, convs: Conversations[]) => {
    const index = convs.findIndex(c => c.id === convId);
    if (index === -1) return 1;
    return Math.floor(index / messagesPerPage) + 1;
  }, []);

  // SINGLE EFFECT: Handle URL params, conversation activation, and pagination
  useEffect(() => {
    // Wait for conversations to load
    if (conversations.length === 0) return;
    
    // Skip if already processed and we have an active conversation
    if (urlParamsProcessed.current && activeConvId) return;

    const params = new URLSearchParams(window.location.search);
    const idFromUrl = params.get("id");

    // No URL param - use default behavior (first conversation)
    if (!idFromUrl) {
      if (!activeConvId) {
        setActiveConvId(conversations[0]?.id ?? null);
      }
      urlParamsProcessed.current = true;
      return;
    }

    const jiraKey = "PRISM-" + idFromUrl;
    const existing = conversations.find(c => c.jiraIssueKey === jiraKey);

    if (existing) {
      // FOUND: Set page and active conversation
      const targetPage = findPageForConversation(existing.id, conversations);
      setCurrentPage(targetPage);
      setActiveConvId(existing.id);
     // console.log("Activated conversation from URL:", existing.id, "on page:", targetPage);
      urlParamsProcessed.current = true;
      creatingConversation.current = false;
    } else if (!creatingConversation.current && !urlParamsProcessed.current) {
      // NOT FOUND: Create new conversation (GUARDED by ref)
      creatingConversation.current = true;
      
      const createFromUrl = async () => {
        try {
          setLoading(true);
          await CreateConversation(
            instance,
            accounts[0],
            "Project issue " + idFromUrl,
            jiraKey
          );
          onChange(); // Refresh conversations list
          // Effect will re-run with new conversations and find the created one
        } catch (e: any) {
          setErr(e.message || "Failed to create conversation from URL parameter.");
          urlParamsProcessed.current = true; // Mark done on error
          creatingConversation.current = false;
        } finally {
          setLoading(false);
        }
      };

      createFromUrl();
    }
    // If creatingConversation is true, we're waiting for onChange() to refresh list
  }, [conversations, activeConvId, instance, accounts, onChange, findPageForConversation]);

  // Check if conversation is closed
  const closedconv = useMemo(() => {
    const conv = conversations.find(c => c.id === activeConvId);
    return conv?.status === 1;
  }, [conversations, activeConvId]);

  // Check if message is mine
  const isMine = useCallback((m: Messages) => {
    const email = (m as any).authorEmail?.toLowerCase?.() ?? '';
    return (
      m.authorUserId === me.accountId ||
      m.authorUserId === me.homeId ||
      (!!email && email === me.username)
    );
  }, [me]);

  // Fetch messages when active conversation changes
  useEffect(() => {
    if (!activeConvId) {
      setMsgs([]);
      return;
    }

    const fetchMessages = async () => {
      try {
        setLoading(true);
        setErr(null);
        const returnmessages = await GetMessages(instance, accounts[0], activeConvId);
        returnmessages.sort((a, b) => 
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        setMsgs(returnmessages);
        // Immediately set unread count to 0 in parent
        if (typeof onUnreadCountUpdate === 'function') {
          onUnreadCountUpdate(activeConvId);
        }
      } catch (e: any) {
        setErr(e.message || 'Something went wrong while fetching messages.');
        setMsgs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [activeConvId, instance, accounts, onUnreadCountUpdate]);

  // Slice conversations for pagination
  const paginatedConversations = useMemo(() => {
    const startIndex = (currentPage - 1) * messagesPerPage;
    return conversations.slice(startIndex, startIndex + messagesPerPage);
  }, [conversations, currentPage]);

  // Handle send message
  const handleSend = async (body: string, file: File | null) => {
    if (!activeConvId) return;

    try {
      setLoading(true);
      setErr(null);
      const newMessage = await postMessageInConversation(
        instance,
        accounts[0],
        activeConvId,
        body,
        file,
        uuidv4()
      );
      const optimisticMessage = {
  ...newMessage,
  authorUserId: me.accountId,
  authorEmail: me.username
};
      setMsgs(prev => [...prev, optimisticMessage]);
    } catch (e: any) {
      setErr(e.message || 'Failed to send the message.');
    } finally {
      setLoading(false);
    }
  };

  // Handle close conversation
  const handleCloseConversation = async (id: string) => {
    try {
      setActionError(null);
      await CloseConversation(instance, accounts[0], id);
      if (id === activeConvId) setActiveConvId(null);
      onChange();
    } catch (error: any) {
      setActionError(error.message || 'Failed to close conversation');
    }
  };

  // Handle delete conversation
  const handleDeleteConversation = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;

    try {
      setActionError(null);
      await DeleteConversation(instance, accounts[0], id);
      if (id === activeConvId) {
        setActiveConvId(null);
        setMsgs([]);
        navigate('/messaging');
      }
      onChange();
    } catch (error: any) {
      setActionError(error.message || 'Failed to delete conversation');
    }
  };

  return (
    <Tab.Container
      activeKey={activeConvId ?? undefined}
      onSelect={(k) => setActiveConvId((k as string) ?? null)}
    >
      <Row className='m-0' style={{ minHeight: '83vh' }}>
        <Col lg={3} md={5} className='border-end p-0 position-relative pb-6'>
          <FadeAlert show={!!actionError} dismissFunction={() => setActionError(null)}>
            {actionError}
          </FadeAlert>
          
          <ConversationList
            conversations={paginatedConversations}
            activeConvId={activeConvId}
            currentPage={currentPage}
            totalPages={totalPages}
            onNewChat={() => setNewChat(true)}
            onCloseConversation={handleCloseConversation}
            onDeleteConversation={handleDeleteConversation}
            onPageChange={setCurrentPage}
            timeAgo={timeAgo}
          />
        </Col>

        <Col lg={9} md={7} className='bg-lighter p-0'>
          <div style={{ height: '83vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <Tab.Content className='px-4 py-3 position-relative'>
                <Tab.Pane eventKey={activeConvId ?? ''}>
                  <ChatHeader isClosed={closedconv} />
                  
                  {err && (
                    <Alert variant="warning" dismissible onClose={() => setErr(null)}>
                      {err}
                    </Alert>
                  )}
                  
                  <MessageList 
                    msgs={msgs} 
                    loading={loading} 
                    err={err} 
                    isMine={isMine} 
                    timeAgo={timeAgo}
                  />
                </Tab.Pane>
              </Tab.Content>
            </div>

            <MessageInput
              activeConvId={activeConvId}
              loading={loading}
              isClosed={closedconv}
              onSend={handleSend}
            />
          </div>
        </Col>
      </Row>

      <NewChatModal
        show={newChat}
        onHide={() => setNewChat(false)}
        onCreated={onChange}
      />
    </Tab.Container>
  );
};

export default Messaging;


